import { Text, Code, Icon, InlineStack, Badge, FormattedDateTime, Spinner } from '@customerio/pluma-components/react';
import type { ActionType, SearchResult } from '@/services/auth/types';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { formatNumber } from '@/utils/number';
import { ACTION_TYPE_ICONS } from '../constants';
import { ActionTypeLabels } from '@/utils/campaign-labels';
import { MetadataRow } from './metadata-row';

type Props = { result: Extract<SearchResult, { kind: 'action' }> };

/** Partial type for the action objects in the getCampaignById response */
interface CampaignAction {
  id: string;
  type: string;
  name: string;
  // Messaging actions
  sending_state?: string;
  subject?: string;
  has_content?: boolean;
  has_subject?: boolean;
  tracked?: boolean;
  send_to_platform?: string;
  // Delay actions
  delay?: number;
  start_time?: string;
  end_time?: string;
  days?: string;
  zone?: string;
  min_delay?: number;
  max_delay?: number;
  // Webhook actions
  request_method?: string;
  hostname?: string;
  has_url?: boolean;
  // A/B test
  allocations?: number[];
  split_state?: string;
  // Conditional branch
  conditions?: string;
  // Multi-split branch
  multi_conditions?: string[];
  // Random cohort branch
  cohorts?: number[];
  cohort_names?: string[];
  // Create event
  event_name?: string;
  // Attribute update
  attribute_name?: string;
  attribute_value?: string;
  // Collection query
  collection_id?: string;
  journey_attribute_name?: string;
  // Manual segment update
  segment_id?: string;
  add_to_seg?: boolean;
  // Wait until
  exit_type?: string;
  // LLM action
  llm_calls?: Array<{ model?: string }>;
  // Conditional wait event attribute
  delay_event_attr?: string;
}

interface ActionStatus {
  id: string;
  subject_count: number;
  eval_stage: string;
  eval_progress: number;
  subject_count_limited: boolean;
}

const SENDING_STATE_CONFIG: Record<string, { label: string; color: 'green' | 'grey' | 'yellow' }> = {
  automatic: { label: 'Automatic', color: 'green' },
  draft: { label: 'Draft', color: 'yellow' },
  off: { label: 'Off', color: 'grey' },
};

export function formatDelayDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const remainingSeconds = seconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
  return parts.join(' ');
}

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function formatDays(days: string): string | null {
  if (isEmptyString(days)) return null;
  try {
    const parsed = JSON.parse(days);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (parsed.length === 7) return 'Every day';
    return parsed.map((d) => WEEKDAY_NAMES[d] ?? `Day ${d}`).join(', ');
  } catch {
    return null;
  }
}

/** Cohort path letter: 0 → A, 1 → B, etc. */
function cohortLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** Messaging action types that have a sending state */
const MESSAGING_ACTION_TYPES = new Set<ActionType>([
  'email_action',
  'twilio_action',
  'push_action',
  'urban_airship_action',
  'slack_action',
  'in_app_action',
  'line_action',
  'inbox_action',
  'whatsapp_action',
]);

type ActionDetailsProps = { action: CampaignAction; actionType: ActionType };

function ActionDetails({ action, actionType }: ActionDetailsProps) {
  const isMessaging = MESSAGING_ACTION_TYPES.has(actionType);
  const sendingState = action.sending_state != null ? SENDING_STATE_CONFIG[action.sending_state] : null;
  const formattedDays = actionType === 'delay_time_window_action' ? formatDays(action.days ?? '') : null;

  return (
    <>
      {/* Messaging actions: sending state + subject */}
      {isMessaging && sendingState != null ? (
        <MetadataRow label="Sending">
          <Badge color={sendingState.color}>{sendingState.label}</Badge>
        </MetadataRow>
      ) : null}
      {actionType === 'email_action' && !isEmptyString(action.subject) ? (
        <MetadataRow label="Subject">
          <Text size="xs" truncate className="fs-mask">
            {action.subject}
          </Text>
        </MetadataRow>
      ) : null}
      {/* Time Delay */}
      {actionType === 'delay_seconds_action' && action.delay != null && action.delay > 0 ? (
        <MetadataRow label="Delay">
          <Text size="xs">{formatDelayDuration(action.delay)}</Text>
        </MetadataRow>
      ) : null}
      {/* Time Window */}
      {actionType === 'delay_time_window_action' ? (
        <>
          {!isEmptyString(action.start_time) && !isEmptyString(action.end_time) ? (
            <MetadataRow label="Window">
              <Text size="xs">
                {action.start_time} – {action.end_time}
              </Text>
            </MetadataRow>
          ) : null}
          {formattedDays != null ? (
            <MetadataRow label="Days">
              <Text size="xs">{formattedDays}</Text>
            </MetadataRow>
          ) : null}
          {!isEmptyString(action.zone) ? (
            <MetadataRow label="Timezone">
              <Text size="xs">{action.zone}</Text>
            </MetadataRow>
          ) : null}
        </>
      ) : null}
      {/* Randomized Delay */}
      {actionType === 'randomized_delay_action' && action.min_delay != null && action.max_delay != null ? (
        <MetadataRow label="Delay">
          <Text size="xs">
            {formatDelayDuration(action.min_delay)} – {formatDelayDuration(action.max_delay)}
          </Text>
        </MetadataRow>
      ) : null}
      {/* Wait Until */}
      {actionType === 'conditional_wait_action' && !isEmptyString(action.exit_type) ? (
        <MetadataRow label="Exit">
          <Text size="xs">{action.exit_type === 'timeout' ? 'On timeout' : 'When condition met'}</Text>
        </MetadataRow>
      ) : null}
      {actionType === 'conditional_wait_action' && !isEmptyString(action.delay_event_attr) ? (
        <MetadataRow label="Wait for">
          <Code size="xs">{action.delay_event_attr}</Code>
        </MetadataRow>
      ) : null}
      {/* Webhook */}
      {actionType === 'webhook_action' && !isEmptyString(action.hostname) ? (
        <MetadataRow label="Webhook">
          <Text size="xs" truncate className="fs-mask">
            {!isEmptyString(action.request_method) ? `${action.request_method} ` : ''}
            {action.hostname}
          </Text>
        </MetadataRow>
      ) : null}
      {/* A/B Test */}
      {actionType === 'split_randomized_action' && action.allocations != null && action.allocations.length > 0 ? (
        <MetadataRow label="Split">
          <Text size="xs">{action.allocations.map((a) => `${a}%`).join(' / ')}</Text>
        </MetadataRow>
      ) : null}
      {actionType === 'split_randomized_action' && !isEmptyString(action.split_state) ? (
        <MetadataRow label="Test">
          <Badge color={action.split_state === 'running' ? 'green' : 'grey'}>
            {action.split_state === 'running'
              ? 'Running'
              : action.split_state === 'winner'
                ? 'Winner chosen'
                : (action.split_state ?? '')}
          </Badge>
        </MetadataRow>
      ) : null}
      {/* Conditional Branch */}
      {actionType === 'conditional_branch_action' && !isEmptyString(action.conditions) ? (
        <MetadataRow label="Branches">
          <Text size="xs">True / False</Text>
        </MetadataRow>
      ) : null}
      {/* Multi-Split Branch */}
      {actionType === 'multi_split_branch_action' && action.multi_conditions != null ? (
        <MetadataRow label="Branches">
          <Text size="xs">{action.multi_conditions.length + 1} paths</Text>
        </MetadataRow>
      ) : null}
      {/* Random Cohort */}
      {actionType === 'random_cohort_branch_action' && action.cohorts != null && action.cohorts.length > 0 ? (
        <MetadataRow label="Cohorts">
          <Text size="xs">{action.cohorts.map((pct, index) => `${cohortLetter(index)}: ${pct}%`).join(', ')}</Text>
        </MetadataRow>
      ) : null}
      {/* Create Event */}
      {actionType === 'create_event_action' && !isEmptyString(action.event_name) ? (
        <MetadataRow label="Event">
          <Code size="xs">{action.event_name}</Code>
        </MetadataRow>
      ) : null}
      {/* Attribute Update */}
      {actionType === 'attribute_update_action' && !isEmptyString(action.attribute_name) ? (
        <MetadataRow label="Attribute">
          <Text size="xs" truncate className="fs-mask">
            <Code size="xs">{action.attribute_name}</Code>
          </Text>
        </MetadataRow>
      ) : null}
      {/* Manual Segment Update */}
      {actionType === 'static_seg_update_action' ? (
        <MetadataRow label="Operation">
          <Text size="xs">{action.add_to_seg === true ? 'Add to segment' : 'Remove from segment'}</Text>
        </MetadataRow>
      ) : null}
      {/* LLM Action */}
      {actionType === 'llm_action' && action.llm_calls != null && action.llm_calls.length > 0 ? (
        <MetadataRow label="Model">
          <Text size="xs">{action.llm_calls[0].model ?? 'Unknown'}</Text>
        </MetadataRow>
      ) : null}
    </>
  );
}

type JourneysOnActionProps = { status: ActionStatus };

function JourneysOnAction({ status }: JourneysOnActionProps) {
  return (
    <MetadataRow label="On action">
      <Text size="xs">
        {status.subject_count_limited ? '>' : ''}
        {formatNumber(status.subject_count)}{' '}
        {status.subject_count === 1 && !status.subject_count_limited ? 'journey' : 'journeys'}
        {status.eval_stage === 'Processing' ? ` (processing ${status.eval_progress}%)` : ''}
      </Text>
    </MetadataRow>
  );
}

export function findAction(
  actions: CampaignAction[] | undefined,
  searchId: string,
  actionType: ActionType,
): CampaignAction | undefined {
  if (actions == null) return undefined;
  // Direct ID match (works when search index and campaign version are in sync)
  const byId = actions.find((a) => a.id === searchId);
  if (byId != null) return byId;
  // Fallback: match by type when there's exactly one action of that type.
  // Search result action_type values match the campaign response type field directly.
  const byType = actions.filter((a) => a.type === actionType);
  return byType.length === 1 ? byType[0] : undefined;
}

export function ActionPreview({ result }: Props) {
  const actionLabel = ActionTypeLabels[result.other.action_type];
  const actionIcon = ACTION_TYPE_ICONS[result.other.action_type];

  const envId = Number(result.other.env_id);
  const campaignId = result.other.campaign_id;
  const canFetchCampaign = envId > 0 && !isEmptyString(campaignId);

  const { data, isLoading } = useHydraUIAPIQuery({
    queryKey: ['getCampaignById', envId, campaignId],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.get({
        params: { path: { environment_id: String(envId), campaign_id: campaignId } },
        signal,
      }),
    queryConfig: { enabled: canFetchCampaign },
  });

  const { data: statusData } = useHydraUIAPIQuery({
    queryKey: ['getCampaignActionStatus', envId, campaignId],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.getActionStatus({
        params: { path: { environment_id: String(envId), campaign_id: campaignId } },
        signal,
      }),
    queryConfig: { enabled: canFetchCampaign },
  });

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const actions = data?.actions as CampaignAction[] | undefined;
  // Search results use versioned action IDs which may differ from the campaign response's
  // current-version IDs. Fall back to matching by type when there's exactly one of that type.
  const action = findAction(actions, result.other.id, result.other.action_type);
  // Use the resolved action ID (which matches action_status IDs) for the status lookup
  const resolvedId = action?.id ?? result.other.id;
  const actionStatus = statusData?.actions?.find((a) => a.id === resolvedId);

  return (
    <>
      <MetadataRow label="ID">
        <Code size="xs">{result.other.id}</Code>
      </MetadataRow>
      {actionLabel != null ? (
        <MetadataRow label="Type">
          <InlineStack gap="050" alignBlock="center">
            {actionIcon != null ? <Icon name={actionIcon} size="sm" color="subtle" /> : null}
            <Text size="xs">{actionLabel}</Text>
          </InlineStack>
        </MetadataRow>
      ) : null}
      {action != null ? (
        <ActionDetails action={action} actionType={result.other.action_type} />
      ) : isLoading ? (
        <MetadataRow label="Details">
          <Spinner />
        </MetadataRow>
      ) : null}
      {actionStatus != null ? <JourneysOnAction status={actionStatus} /> : null}
      <MetadataRow label="Created">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
        </Text>
      </MetadataRow>
      <MetadataRow label="Updated">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
        </Text>
      </MetadataRow>
    </>
  );
}
